import React, { useState } from 'react';
import { useApi, guestPhotos as guestPhotosApi, uploadApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['All', 'Beach', 'Pool', 'Room', 'View', 'Restaurant', 'Exterior', 'Other'];

export default function GuestPhotos({ propertyId }) {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('All');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ category: 'Other', caption: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const fetchCategory = activeCategory === 'All' ? undefined : activeCategory;
  const { data, loading, refetch } = useApi(
    propertyId ? `/properties/${propertyId}/guest-photos${fetchCategory ? `?category=${fetchCategory}` : ''}` : null,
    [activeCategory]
  );
  const photos = data?.photos || [];

  async function handleUpload(e) {
    e.preventDefault();
    setUploadError('');
    if (!uploadFile) { setUploadError('Please select a photo.'); return; }

    setUploading(true);
    try {
      const res = await uploadApi.images([uploadFile]);
      const url = res.data.urls[0];
      await guestPhotosApi.upload(propertyId, { url, category: uploadForm.category, caption: uploadForm.caption });
      setUploadSuccess(true);
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ category: 'Other', caption: '' });
      refetch();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ marginTop: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, margin: 0 }}>Guest Photos</h2>
        {user && !showUpload && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => { setShowUpload(true); setUploadSuccess(false); }}
          >
            + Add Photo
          </button>
        )}
      </div>

      {uploadSuccess && (
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem', marginBottom: '1rem', color: 'var(--success)', fontSize: 'var(--text-base)' }}>
          Photo submitted! It will appear once approved.
        </div>
      )}

      {showUpload && (
        <form onSubmit={handleUpload} style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--text-base)', marginBottom: '0.35rem' }}>Category</label>
            <select
              value={uploadForm.category}
              onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
              style={{ width: '100%', padding: '0.45rem 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', fontSize: 'var(--text-base)' }}
            >
              {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--text-base)', marginBottom: '0.35rem' }}>Photo</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={e => setUploadFile(e.target.files[0] || null)}
              style={{ fontSize: 'var(--text-base)' }}
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--text-base)', marginBottom: '0.35rem' }}>Caption (optional)</label>
            <input
              type="text"
              maxLength={255}
              placeholder="A short description…"
              value={uploadForm.caption}
              onChange={e => setUploadForm(f => ({ ...f, caption: e.target.value }))}
              style={{ width: '100%', padding: '0.45rem 0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', fontSize: 'var(--text-base)', boxSizing: 'border-box' }}
            />
          </div>
          {uploadError && <p style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', margin: '0 0 0.75rem' }}>{uploadError}</p>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary btn-sm" type="submit" disabled={uploading}>
              {uploading ? 'Uploading…' : 'Submit Photo'}
            </button>
            <button className="btn btn-outline btn-sm" type="button" onClick={() => setShowUpload(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Category filter bar */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 999,
              border: '1.5px solid',
              fontSize: 'var(--text-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
              borderColor: activeCategory === cat ? '#2563EB' : 'var(--border-strong)',
              background: activeCategory === cat ? '#2563EB' : 'white',
              color: activeCategory === cat ? 'white' : 'var(--ink)',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: 'var(--ink-faint)', fontSize: 'var(--text-base)' }}>Loading photos…</p>}

      {!loading && photos.length === 0 && (
        <p style={{ color: 'var(--ink-faint)', fontSize: 'var(--text-base)', fontStyle: 'italic' }}>
          No guest photos yet in this category. {user ? 'Be the first to share one!' : 'Log in to add yours.'}
        </p>
      )}

      {photos.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '0.75rem',
        }}>
          {photos.map(photo => (
            <div key={photo.id} style={{ position: 'relative', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--surface-alt)' }}>
              <img
                src={photo.url}
                alt={photo.caption || photo.category}
                loading="lazy"
                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
              />
              <div style={{ padding: '0.4rem 0.6rem' }}>
                <span style={{
                  fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--ink-muted)',
                  background: 'var(--surface-alt)', borderRadius: 'var(--radius-sm)', padding: '0.1rem 0.35rem',
                }}>
                  {photo.category}
                </span>
                {photo.caption && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--ink)', margin: '0.25rem 0 0', lineHeight: 1.3 }}>
                    {photo.caption}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
